import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Form } from 'react-bootstrap';
import Calendar from './calendar.component';
import { PRICING_TYPE_OPTIONS } from './pricing-type-options.constants';
import { Tooltip } from './common/tooltip.component';
import ModalPensionHolidaySection from './ModalPensionHolidaySection';
import ModalGenerateReview from './ModalGenerateReview';
import { roundToQuarterHour } from '../utils/timeConversion.utils';
import { sanitizePriceInput } from '../helper/common.helper';
import '../scss/create-invoice.scss';
import '../pages/timesheet/timeSheetsPage/timeSheetsPage.scss';
interface ModalFormData {
  hoursPerDay: string;
  hourlyPrice: string;
  showDates: string;
  serviceDescription: string;
  pricingType: string;
}

interface AutoGenerateItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  freelancerId: string;
  freelancerName: string;
  onGenerate: (modalData: ModalFormData, selectedDates: Date[]) => void;
  isRegularEngagement: boolean;
  currency: string;
  t: (key: string) => string;
  showDatesOptions: Array<{ label: string; value: string }>;
  initialData?: ModalFormData;
  initialSelectedDates?: Date[];
  // NEW (optional) — global "Add multiple rows": pick which freelancer(s) get the rows
  showFreelancerSelector?: boolean;
  freelancerOptions?: Array<{ value: string; label: string }>;
  // Default target for the "Choose freelancer" selector — the tab the user is on
  initialFreelancerTarget?: string;
  onGenerateForFreelancer?: (freelancerValue: string, modalData: ModalFormData, selectedDates: Date[]) => void;
  existingDatesByFreelancer?: { [uid: string]: { name: string; dates: string[] } };
  onRemoveExistingDate?: (uid: string, date: string) => void;
  // NEW (optional) — pension & holiday controls for a specific freelancer (global modal only)
  globalData?: any;
  pensionData?: { [id: string]: { pension: boolean; pensionAmount: number } };
  holidayData?: { [id: string]: { holidaySavings: boolean; holidaySavingsFee: number } };
  onPensionChange?: (data: any) => void;
  onHolidayChange?: (data: any) => void;
  calculatedSubAmount?: any;
  // Existing rows per freelancer, used by the review step (global modal only)
  existingRowsByFreelancer?: { [uid: string]: any[] };
}

// Parse a stored MM/DD/YYYY (or YYYY-MM-DD) string into a Date
const parseStoredDate = (d: string): Date | null => {
  if (!d) return null;
  const parts = d.split(/[/-]/).map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  // stored as MM/DD/YYYY
  const [a, b, c] = parts;
  const dt = c > 31 ? new Date(c, (a || 1) - 1, b || 1) : new Date(a, (b || 1) - 1, c || 1);
  return isNaN(dt.getTime()) ? null : dt;
};

const AutoGenerateItemsModal: React.FC<AutoGenerateItemsModalProps> = ({
  isOpen,
  onClose,
  freelancerId,
  freelancerName,
  onGenerate,
  isRegularEngagement,
  currency,
  t,
  showDatesOptions,
  initialData,
  initialSelectedDates,
  showFreelancerSelector = false,
  freelancerOptions = [],
  initialFreelancerTarget = 'all',
  onGenerateForFreelancer,
  existingDatesByFreelancer = {},
  onRemoveExistingDate,
  globalData,
  pensionData = {},
  holidayData = {},
  onPensionChange,
  onHolidayChange,
  calculatedSubAmount,
  existingRowsByFreelancer = {},
}) => {
  // Selected freelancer target for the global selector ('all' or a freelancer id).
  // Defaults to the tab the user opened the modal from (initialFreelancerTarget).
  const [selectedFreelancerTarget, setSelectedFreelancerTarget] = useState<string>(initialFreelancerTarget || 'all');
  // Re-seed the target to the current tab each time the modal opens.
  useEffect(() => {
    if (isOpen) setSelectedFreelancerTarget(initialFreelancerTarget || 'all');
  }, [isOpen, initialFreelancerTarget]);
  const [isFreelancerDropdownOpen, setIsFreelancerDropdownOpen] = useState(false);
  const freelancerSelectRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showFreelancerSelector) return;
    const handler = (e: MouseEvent) => {
      if (freelancerSelectRef.current && !freelancerSelectRef.current.contains(e.target as Node)) {
        setIsFreelancerDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showFreelancerSelector]);
  // Form state - initialize with initialData if provided
  const [formData, setFormData] = useState<ModalFormData>(initialData || {
    hoursPerDay: '08:00',
    hourlyPrice: '',
    showDates: 'yes',
    serviceDescription: '',
    pricingType: 'Tim',
  });

  const [selectedDates, setSelectedDates] = useState<Date[]>(initialSelectedDates || []);
  const [workDatesDisplay, setWorkDatesDisplay] = useState<string>('');
  const [showInternalCalendar, setShowInternalCalendar] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isMobile, setIsMobile] = useState(false);

  // Display-only: remembers the exact separator the user typed for Hours per Day (',' '.' or ':').
  // formData.hoursPerDay itself always stays in canonical HH:MM (colon) form so validation,
  // the estimated-total calc and item generation are unaffected.
  const [rawHoursPerDay, setRawHoursPerDay] = useState<string | undefined>(undefined);
  
  // Dropdown states
  const [isPricingTypeDropdownOpen, setIsPricingTypeDropdownOpen] = useState(false);
  // Themed minutes dropdown (00/15/30/45) for "Hours per day"
  const [isMinuteDropdownOpen, setIsMinuteDropdownOpen] = useState(false);
  const [minMenuPos, setMinMenuPos] = useState<{ left: number; top: number; width: number } | null>(null);
  const minuteTriggerRef = useRef<HTMLDivElement>(null);
  const minuteMenuRef = useRef<HTMLDivElement>(null);
  // Position the portaled minutes menu relative to its trigger, flipping up when
  // there isn't room below (fixed positioning so the modal's scroll/overflow can't clip it).
  const openMinuteMenu = () => {
    const el = minuteTriggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const MENU_H = 192;
    const spaceBelow = window.innerHeight - r.bottom;
    const top = spaceBelow >= MENU_H + 12 ? r.bottom + 6 : Math.max(8, r.top - MENU_H - 6);
    setMinMenuPos({ left: r.left, top, width: r.width });
    setIsMinuteDropdownOpen(true);
  };
  
  // Refs
  const pricingTypeDropdownRef = useRef<HTMLDivElement>(null);
  const dateTriggerRef = useRef<HTMLDivElement>(null);

  // Calendar popover position (fixed, opens upward so it's never clipped)
  const [calPos, setCalPos] = useState<{ left: number; top?: number; bottom?: number; scale: number; wrapW: number; wrapH: number } | null>(null);

  // ── Draft pension/holiday ─────────────────────────────────────────────────
  // Edits inside the modal are staged locally and ONLY committed to the real
  // pensionData/holidayData when the user clicks "Add Rows". Cancelling discards
  // them, so nothing changes until the user confirms.
  const [draftPensionData, setDraftPensionData] = useState(pensionData);
  const [draftHolidayData, setDraftHolidayData] = useState(holidayData);
  useEffect(() => {
    if (isOpen) {
      setDraftPensionData(pensionData);
      setDraftHolidayData(holidayData);
    }
    // Only re-seed the drafts when the modal opens (not on every parent render)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ── Wizard stepper (global modal only) ────────────────────────────────────
  const [activeStep, setActiveStep] = useState(0);
  const isAllTarget = selectedFreelancerTarget === 'all';
  // Pension/Holiday step only exists for a specific freelancer (hidden on "All"),
  // and only in a regular engagement (otherwise both controls are hidden → empty step)
  const showPensionStep = !isAllTarget && isRegularEngagement;
  const stepKeys = useMemo(
    () => ['who', 'work', ...(showPensionStep ? ['pension'] : []), 'service', 'review'],
    [showPensionStep]
  );
  const activeKey = stepKeys[Math.min(activeStep, stepKeys.length - 1)];

  // Reset to first step whenever the modal opens
  useEffect(() => {
    if (isOpen) setActiveStep(0);
  }, [isOpen]);
  // Keep the index valid when the step count changes (target switches all ⇄ specific)
  useEffect(() => {
    setActiveStep((s) => Math.min(s, stepKeys.length - 1));
  }, [stepKeys.length]);

  // Each step wraps existing field blocks with display:contents so the non-global
  // modal layout is untouched and all local form state persists across steps.
  const paneStyle = (key: string): React.CSSProperties => {
    if (!showFreelancerSelector) return { display: key === 'review' ? 'none' : 'contents' };
    return { display: activeKey === key ? 'contents' : 'none' };
  };

  const stepLabel = (key: string): string => {
    switch (key) {
      case 'who':
        return t('STEP_FREELANCER_DATES') || 'Freelancer & Dates';
      case 'work':
        return t('STEP_WORK_DETAILS') || 'Work Details';
      case 'pension':
        return t('STEP_PENSION') || 'Pension & Holiday';
      case 'service':
        return t('STEP_SERVICE') || 'Service';
      case 'review':
        return t('REVIEW') || 'Review';
      default:
        return key;
    }
  };

  // Validate only the fields belonging to the given step
  const validateStep = (key: string): boolean => {
    const e: { [k: string]: string } = {};
    if (key === 'who') {
      if (selectedDates.length === 0) e.dates = t('PLEASE_SELECT_DATES') || 'Please select at least one date';
    } else if (key === 'work') {
      if (!formData.hoursPerDay || formData.hoursPerDay.trim() === '') {
        e.hoursPerDay = t('HOURS_REQUIRED') || 'Hours per day is required';
      } else if (formData.pricingType !== 'Tim') {
        if (!/^[1-9]\d*$/.test(formData.hoursPerDay)) {
          e.hoursPerDay = t('QUANTITY_INVALID_FORMAT') || 'Invalid quantity. Enter a positive integer';
        }
      } else if (!/^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/.test(formData.hoursPerDay)) {
        e.hoursPerDay = t('HOURS_INVALID_FORMAT') || 'Invalid time format. Use hh:mm';
      }
      if (!formData.hourlyPrice || formData.hourlyPrice.trim() === '') {
        e.hourlyPrice = t('PRICE_REQUIRED') || 'Hourly price is required';
      } else if (parseFloat(formData.hourlyPrice) <= 0) {
        e.hourlyPrice = t('PRICE_MUST_BE_POSITIVE') || 'Price must be greater than 0';
      }
    } else if (key === 'service') {
      if (!formData.serviceDescription.trim()) {
        e.serviceDescription = t('TASK_DETAILS_REQUIRED') || 'Service description is required';
      } else {
        const latinRegex = /^[A-Za-zÅÄÖåäö0-9\s\.,!?'"\-()&]*$/;
        if (!latinRegex.test(formData.serviceDescription)) {
          e.serviceDescription = t('ONLY_LATIN_CHARACTERS_ALLOWED') || 'Only Latin characters are allowed';
        } else if (formData.serviceDescription.length > 42) {
          e.serviceDescription = t('SERVICE_DESCRIPTION_MAX_CHARS') || 'Maximum 42 characters allowed';
        }
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (activeKey === 'review') {
      handleGenerate();
      return;
    }
    if (validateStep(activeKey)) {
      setActiveStep((s) => Math.min(s + 1, stepKeys.length - 1));
    }
  };
  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));
  const goToStep = (i: number) => {
    if (i <= activeStep) setActiveStep(i); // only allow jumping back to visited steps
  };

  // Update form when initialData changes (when modal reopens)
  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData(initialData);
      }
      if (initialSelectedDates && initialSelectedDates.length > 0) {
        setSelectedDates(initialSelectedDates);
        setWorkDatesDisplay(formatDateRange(initialSelectedDates));
      }
    }
  }, [isOpen, initialData, initialSelectedDates]);

  // Hours per day vs Unit when the pricing type changes:
  //  • Per hour ('Tim') → time (HH:MM); keep a valid time, else default 08:00.
  //  • Any other type   → a single "unit" quantity (integer, default 1).
  useEffect(() => {
    if (formData.pricingType !== 'Tim') {
      setFormData(prev => ({ ...prev, hoursPerDay: '1' }));
    } else {
      setFormData(prev => (/^\d{1,2}:\d{2}$/.test(prev.hoursPerDay) ? prev : { ...prev, hoursPerDay: '08:00' }));
    }
  }, [formData.pricingType]);

  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Keep the portaled minutes menu anchored to its trigger when the modal body
  // scrolls or the window resizes (fixed positioning otherwise goes stale).
  useEffect(() => {
    if (!isMinuteDropdownOpen) return;
    const reposition = () => {
      const el = minuteTriggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const MENU_H = 192;
      const spaceBelow = window.innerHeight - r.bottom;
      const top = spaceBelow >= MENU_H + 12 ? r.bottom + 6 : Math.max(8, r.top - MENU_H - 6);
      setMinMenuPos({ left: r.left, top, width: r.width });
    };
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isMinuteDropdownOpen]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pricingTypeDropdownRef.current && !pricingTypeDropdownRef.current.contains(event.target as Node)) {
        setIsPricingTypeDropdownOpen(false);
      }
      if (
        minuteTriggerRef.current && !minuteTriggerRef.current.contains(event.target as Node) &&
        (!minuteMenuRef.current || !minuteMenuRef.current.contains(event.target as Node))
      ) {
        setIsMinuteDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keep the calendar popover anchored above the date field (opens upward, fixed
  // so it can never be clipped by the scrollable modal body). Recompute on scroll/resize.
  useEffect(() => {
    if (!showInternalCalendar) return;
    const update = () => {
      const el = dateTriggerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Natural inline-calendar size (from calendar.scss .calendar-inline).
      const CAL_W = 480, CAL_H = 470;
      const isMob = window.innerWidth <= 640;
      // Open toward whichever side has more room (flip up/down).
      const spaceAbove = r.top - 12;
      const spaceBelow = window.innerHeight - r.bottom - 12;
      const openUp = spaceAbove >= spaceBelow;
      // Reserve room for the popover padding + the hint bar shown below the
      // calendar, so the whole thing (calendar + hint) fits and the top isn't clipped.
      const CHROME = 72;
      const availH = Math.max(230, (openUp ? spaceAbove : spaceBelow) - CHROME);
      const availW = Math.min(window.innerWidth - 24, Math.max(r.width, CAL_W));
      // Scale down so the calendar ALWAYS fits the available height & width.
      const base = isMob ? 0.95 : 1;
      const scale = Math.max(0.5, Math.min(base, (availH - 20) / CAL_H, (availW - 4) / CAL_W));
      const wrapW = Math.round(CAL_W * scale);
      const wrapH = Math.round(CAL_H * scale);
      // Keep the popover fully on-screen horizontally.
      let left = r.left;
      if (left + wrapW + 20 > window.innerWidth) left = window.innerWidth - wrapW - 20;
      if (left < 10) left = 10;
      if (openUp) {
        setCalPos({ left, bottom: window.innerHeight - r.top + 8, scale, wrapW, wrapH });
      } else {
        setCalPos({ left, top: r.bottom + 8, scale, wrapW, wrapH });
      }
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true); // capture: also catches modal-body scroll
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [showInternalCalendar]);

  // Close the (portaled) calendar when clicking outside the field or the popover.
  // Clicks inside the popover are ignored so multi-date selection keeps working.
  useEffect(() => {
    if (!showInternalCalendar) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (dateTriggerRef.current && dateTriggerRef.current.contains(target)) return;
      if (target.closest && target.closest('.ag-cal-popover')) return;
      setShowInternalCalendar(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showInternalCalendar]);

  // Format dates for display
  const formatDateRange = (dates: Date[]) => {
    if (dates.length === 0) return '';
    
    const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
    const startDate = sortedDates[0];
    const endDate = sortedDates[sortedDates.length - 1];
    
    const formatDate = (date: Date) => {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${year}/${month}/${day}`;
    };
    
    if (dates.length === 1) {
      return formatDate(startDate);
    }
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  // Handle date selection from calendar
  const handleDateSelect = (startDate: Date, endDate?: Date, dates?: Date[]) => {
    const selectedDateArray = dates || (startDate ? [startDate] : []);
    setSelectedDates(selectedDateArray);
    setWorkDatesDisplay(formatDateRange(selectedDateArray));
    // Don't close calendar - let user select multiple dates
    
    // Clear date error if dates are selected
    if (selectedDateArray.length > 0 && errors.dates) {
      setErrors(prev => ({ ...prev, dates: '' }));
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Hours per day validation
    if (!formData.hoursPerDay || formData.hoursPerDay.trim() === '') {
      newErrors.hoursPerDay = t('HOURS_REQUIRED') || 'Hours per day is required';
    } else if (formData.pricingType !== 'Tim') {
      // Non-hourly (per unit / week / month / project): a positive integer unit
      const intRegex = /^[1-9]\d*$/;
      if (!intRegex.test(formData.hoursPerDay)) {
        newErrors.hoursPerDay = t('QUANTITY_INVALID_FORMAT') || 'Invalid quantity. Enter a positive integer';
      }
    } else {
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
      if (!timeRegex.test(formData.hoursPerDay)) {
        newErrors.hoursPerDay = t('HOURS_INVALID_FORMAT') || 'Invalid time format. Use hh:mm';
      }
    }

    // Hourly price validation
    if (!formData.hourlyPrice || formData.hourlyPrice.trim() === '') {
      newErrors.hourlyPrice = t('PRICE_REQUIRED') || 'Hourly price is required';
    } else if (parseFloat(formData.hourlyPrice) <= 0) {
      newErrors.hourlyPrice = t('PRICE_MUST_BE_POSITIVE') || 'Price must be greater than 0';
    }

    // Dates validation
    if (selectedDates.length === 0) {
      newErrors.dates = t('PLEASE_SELECT_DATES') || 'Please select at least one date';
    }

    // Service description validation
    if (!formData.serviceDescription.trim()) {
      newErrors.serviceDescription = t('TASK_DETAILS_REQUIRED') || 'Service description is required';
    } else {
      const latinRegex = /^[A-Za-zÅÄÖåäö0-9\s\.,!?'"\-()&]*$/;
      if (!latinRegex.test(formData.serviceDescription)) {
        newErrors.serviceDescription = t('ONLY_LATIN_CHARACTERS_ALLOWED') || 'Only Latin characters are allowed';
      } else if (formData.serviceDescription.length > 42) {
        newErrors.serviceDescription = t('SERVICE_DESCRIPTION_MAX_CHARS') || 'Maximum 42 characters allowed';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle generate
  const handleGenerate = () => {
    if (!validateForm()) {
      return;
    }

    if (showFreelancerSelector && onGenerateForFreelancer) {
      // Commit the staged pension/holiday edits now that the user confirmed
      if (onPensionChange) onPensionChange(draftPensionData);
      if (onHolidayChange) onHolidayChange(draftHolidayData);
      onGenerateForFreelancer(selectedFreelancerTarget, formData, selectedDates);
    } else {
      onGenerate(formData, selectedDates);
    }
    onClose();
    
    // Reset form
    setFormData({
      hoursPerDay: '08:00',
      hourlyPrice: '',
      showDates: 'yes',
      serviceDescription: '',
      pricingType: 'Tim',
    });
    setSelectedDates([]);
    setWorkDatesDisplay('');
    setErrors({});
    setActiveStep(0);
  };

  // Handle cancel
  const handleCancel = () => {
    onClose();
    
    // Reset form
    setFormData({
      hoursPerDay: '08:00',
      hourlyPrice: '',
      showDates: 'yes',
      serviceDescription: '',
      pricingType: 'Tim',
    });
    setSelectedDates([]);
    setWorkDatesDisplay('');
    setErrors({});
    setActiveStep(0);
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <style>{`
        @media (max-width: 768px) {
          .auto-generate-modal-container {
            padding: 8px !important;
          }
          .auto-generate-modal {
            width: 100% !important;
            max-width: 100% !important;
            border-radius: 12px !important;
            margin: 0 !important;
          }
          .auto-generate-modal-header {
            padding: 16px !important;
          }
          .auto-generate-modal-header h3 {
            font-size: 18px !important;
          }
          .auto-generate-modal-header p {
            font-size: 13px !important;
          }
          .auto-generate-modal-body {
            padding: 16px !important;
          }
          .auto-generate-grid-2col {
            grid-template-columns: 1fr !important;
          }
          .auto-generate-footer {
            padding: 12px 16px !important;
            flex-direction: column !important;
          }
          .auto-generate-footer button {
            width: 100% !important;
            justify-content: center !important;
          }
        }
        @media (max-width: 480px) {
          .auto-generate-modal-header h3 {
            font-size: 16px !important;
          }
          .auto-generate-modal-header p {
            font-size: 12px !important;
          }
          .date-chips-container {
            max-height: 120px !important;
          }
        }
      `}</style>
      <div
        className="auto-generate-modal-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '16px',
        }}
      >
        <div
          className="auto-generate-modal"
          style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '854px',
            maxWidth: '90%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.12), 0px 2px 4px 0px rgba(0, 0, 0, 0.08)',
            fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          }}
        >
        {/* Header - Fixed */}
        <div className="auto-generate-modal-header" style={{ 
          padding: '12px 16px', 
          borderBottom: '1px solid #eaebf0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <h3 style={{ 
              margin: 0, 
              fontSize: '16px', 
              fontWeight: 600,
              lineHeight: 'normal',
              color: '#000000',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: '-0.16px'
            }}>
              {t('AUTO_GENERATE_ITEMS') || 'Auto Generate Line Items'}
            </h3>
            <p style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 300,
              lineHeight: '1.4',
              color: '#252b37',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: '0.07px'
            }}>
              {t('AUTO_GENERATE_DESCRIPTION') || 'Configure the parameters to automatically generate invoice line items for selected dates.'}
            </p>
          </div>
          <button
            onClick={handleCancel}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              flexShrink: 0
            }}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Body - Scrollable */}
        <div className="auto-generate-modal-body" style={{ 
          padding: '24px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '24px',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0
        }}>

          {/* Wizard step indicator (global modal only) */}
          {showFreelancerSelector && (
            <div className="ag-stepper" role="tablist">
              {stepKeys.map((k, i) => (
                <React.Fragment key={k}>
                  <button
                    type="button"
                    className={`ag-step${i === activeStep ? ' active' : ''}${i < activeStep ? ' done' : ''}`}
                    onClick={() => goToStep(i)}
                    disabled={i > activeStep}
                    role="tab"
                    aria-selected={i === activeStep}
                  >
                    <span className="ag-step-index">
                      {i < activeStep ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </span>
                    <span className="ag-step-label">{stepLabel(k)}</span>
                  </button>
                  {i < stepKeys.length - 1 && <span className={`ag-step-line${i < activeStep ? ' done' : ''}`} />}
                </React.Fragment>
              ))}
            </div>
          )}

          {/* STEP: Freelancer & Dates */}
          <div style={paneStyle('who')}>

 {/* Global-only: choose which freelancer(s) receive the generated rows */}
          {showFreelancerSelector && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{ color: '#252525', fontFamily: '"Plus Jakarta Sans"', fontSize: '14px', fontWeight: 600, lineHeight: '20px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                {t('CHOOSE_FREELANCER') || 'Choose freelancer'}
                <Tooltip content={t('CHOOSE_FREELANCER_TOOLTIP') || 'Select which freelancer to add these rows to. Choose "All" to add them to every freelancer.'} position="top">
                  <span className="info-icon" style={{ color: '#7c3aed', cursor: 'pointer' }}>ⓘ</span>
                </Tooltip>
              </label>
              <div className="ag-freelancer-select" ref={freelancerSelectRef} style={{ position: 'relative' }}>
                <div className={`ag-fs-trigger${isFreelancerDropdownOpen ? ' open' : ''}`} onClick={() => setIsFreelancerDropdownOpen((o) => !o)}>
                  <span>{freelancerOptions.find((o) => o.value === selectedFreelancerTarget)?.label || (t('ALL') || 'All')}</span>
                  <svg className={`ag-fs-arrow${isFreelancerDropdownOpen ? ' open' : ''}`} width="18" height="18" viewBox="0 0 20 20" fill="none"><path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                {isFreelancerDropdownOpen && (
                  <div className="ag-fs-list">
                    {freelancerOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`ag-fs-item${selectedFreelancerTarget === opt.value ? ' selected' : ''}`}
                        onClick={() => { setSelectedFreelancerTarget(opt.value); setIsFreelancerDropdownOpen(false); }}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

 {/* Select Dates - Full Width */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{
              color: 'var(--text-icons-light-base-main, #252525)',
              fontFamily: '"Plus Jakarta Sans"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '20px',
              display: 'flex',
              alignItems: 'center',
              // gap: '6px'
            }}>
              {t('SELECT_DATES') || 'Select Dates'}
              {/* <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span> */}
              <Tooltip content={t('DATES_TOOLTIP') || 'Indicate the dates when the work was performed.'} position="top">
                <span style={{ 
                  marginLeft: '4px', 
                  color: 'var(--Brand-500, #6750B8)', 
                  fontFamily: '"Plus Jakarta Sans"', 
                  fontSize: '14px', 
                  fontStyle: 'normal',
                  fontWeight: 700, 
                  lineHeight: '20px'
                }}>ⓘ</span>
              </Tooltip>
            </label>
            <div style={{ position: 'relative' }}>
              <div
                ref={dateTriggerRef}
                style={{
                  height: '44px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: errors.dates ? '1px solid #dc3545' : '1px solid #eaebf0',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: workDatesDisplay ? '#252525' : '#68727d',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setShowInternalCalendar(!showInternalCalendar)}
                onMouseEnter={(e) => {
                  if (!errors.dates) {
                    e.currentTarget.style.borderColor = '#b8b0d4';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = errors.dates ? '#dc3545' : '#eaebf0';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M2.5 8.33073H17.5M6.66667 4.9974V1.66406M13.3333 4.9974V1.66406M7.83333 18.3307H12.1667C14.0335 18.3307 14.9669 18.3307 15.68 17.9674C16.3072 17.6478 16.8171 17.1379 17.1367 16.5107C17.5 15.7977 17.5 14.8642 17.5 12.9974V8.66406C17.5 6.79722 17.5 5.8638 17.1367 5.15076C16.8171 4.52355 16.3072 4.01362 15.68 3.69404C14.9669 3.33073 14.0335 3.33073 12.1667 3.33073H7.83333C5.96649 3.33073 5.03307 3.33073 4.32003 3.69404C3.69282 4.01362 3.18289 4.52355 2.86331 5.15076C2.5 5.8638 2.5 6.79722 2.5 8.66406V12.9974C2.5 14.8642 2.5 15.7977 2.86331 16.5107C3.18289 17.1379 3.69282 17.6478 4.32003 17.9674C5.03307 18.3307 5.96649 18.3307 7.83333 18.3307Z" stroke="#68727D" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>{workDatesDisplay || t('CLICK_TO_SELECT_DATES') || 'Click to select dates'}</span>
                </div>
              </div>
              {showInternalCalendar && calPos && createPortal(
                <div
                  className="ag-cal-popover"
                  style={{
                    position: 'fixed',
                    left: calPos.left,
                    // Anchor above (bottom) or below (top) the field — whichever has more room.
                    ...(calPos.bottom !== undefined ? { bottom: calPos.bottom } : { top: calPos.top }),
                    zIndex: 100002,
                    background: '#fff',
                    borderRadius: '14px',
                    border: '1px solid #eaebf0',
                    boxShadow: '0 12px 40px rgba(124, 58, 237, 0.18)',
                    padding: '8px',
                  }}
                >
                  {/* Fixed box == the scaled calendar size, so layout matches the
                      visual (no clipping); the calendar is scaled to fit the
                      available space on any screen. */}
                  <div style={{ width: calPos.wrapW, height: calPos.wrapH, overflow: 'hidden', position: 'relative' }}>
                    <div style={{ transform: `scale(${calPos.scale})`, transformOrigin: 'top left', width: 480, height: 470 }}>
                      <Calendar
                        isOpen={showInternalCalendar}
                        onClose={() => setShowInternalCalendar(false)}
                        onDateSelect={handleDateSelect}
                        initialSelectedDates={selectedDates}
                        allowRange={false}
                        allowMultiple={true}
                        inline={true}
                        highlightedDates={
                          selectedFreelancerTarget !== 'all' && existingDatesByFreelancer[selectedFreelancerTarget]
                            ? (existingDatesByFreelancer[selectedFreelancerTarget].dates || [])
                                .map((d) => parseStoredDate(d))
                                .filter((d): d is Date => d !== null)
                            : []
                        }
                      />
                    </div>
                  </div>
                  {/* Hint shown below the calendar (not over the grid) until a date is picked */}
                  {selectedDates.length === 0 && (
                    <div style={{
                      width: calPos.wrapW,
                      boxSizing: 'border-box',
                      marginTop: '6px',
                      padding: '6px 10px',
                      borderRadius: '8px',
                      background: '#f4efff',
                      color: '#6b4fd0',
                      fontSize: '11.5px',
                      fontWeight: 600,
                      lineHeight: 1.35,
                      textAlign: 'center',
                      fontFamily: "'Plus Jakarta Sans', sans-serif",
                    }}>
                      {t('CALENDAR_TOOLTIP') || 'Click to select a date. Drag to select multiple dates.'}
                    </div>
                  )}
                </div>,
                document.body
              )}
            </div>
            {errors.dates && (
              <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                {errors.dates}
              </div>
            )}

            {/* Selected Dates Chips */}
            {selectedDates.length > 0 && (
              <div className="date-chips-container" style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginTop: '8px',
                maxHeight: '150px',
                overflowY: 'auto'
              }}>
                {selectedDates.map((date, index) => (
                  <div
                    key={index}
                    style={{
                      height: '36px',
                      padding: '8px 12px',
                      borderRadius: '20px',
                      background: '#edebf5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: 500,
                      color: '#544196',
                      fontFamily: "'Plus Jakarta Sans', sans-serif"
                    }}
                  >
                    <span>{formatDateRange([new Date(date)])}</span>
                    <button
                      onClick={() => {
                        const newDates = selectedDates.filter((_, i) => i !== index);
                        setSelectedDates(newDates);
                        setWorkDatesDisplay(formatDateRange(newDates.map(d => new Date(d))));
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#544196'
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Already-added dates for the selected freelancer(s) — read-only info */}
          {showFreelancerSelector && (() => {
            const items: Array<{ date: string; name?: string; uid: string }> = [];
            if (selectedFreelancerTarget === 'all') {
              Object.keys(existingDatesByFreelancer).forEach((uid) => {
                const entry = existingDatesByFreelancer[uid];
                (entry?.dates || []).forEach((d) => items.push({ date: d, name: entry.name, uid }));
              });
            } else {
              const entry = existingDatesByFreelancer[selectedFreelancerTarget];
              (entry?.dates || []).forEach((d) => items.push({ date: d, uid: selectedFreelancerTarget }));
            }
            if (items.length === 0) return null;
            const fmt = (d: string) => {
              const parts = d.split(/[/-]/);
              if (parts.length === 3) {
                const [mm, dd, yyyy] = parts.map((p) => parseInt(p, 10));
                const dt = new Date(yyyy, (mm || 1) - 1, dd || 1);
                if (!isNaN(dt.getTime())) return dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
              }
              return d;
            };
            return (
              <div className="ag-already-added">
                <span className="ag-already-label">{t('ALREADY_ADDED_DATES') || 'Already added — no need to re-select these dates'}</span>
                <div className="ag-already-badges">
                  {items.map((it, i) => (
                    <span className="ag-already-badge" key={i}>
                      {fmt(it.date)}{it.name ? <em className="ag-already-name">{it.name}</em> : null}
                      {onRemoveExistingDate && (
                        <button
                          type="button"
                          className="ag-already-remove"
                          aria-label={`${t('REMOVE') || 'Remove'} ${fmt(it.date)}`}
                          title={t('REMOVE_WORKING_HOUR') || 'Remove this working hour'}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveExistingDate(it.uid, it.date);
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          </div>{/* END STEP: who */}

          {/* STEP: Work Details */}
          <div style={paneStyle('work')}>

          {/* Hours per Day and Hourly Price - Two Columns */}
          <div className="auto-generate-grid-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '10px', marginBottom: '4px' }}>
            {/* Hours per day */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                color: 'var(--text-icons-light-base-main, #252525)',
                fontFamily: '"Plus Jakarta Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '20px',
                display: 'flex',
                alignItems: 'center',
                // gap: '6px'
              }}>
                {formData.pricingType === 'Tim' ? (t('HOURS_PER_DAY') || 'Hours per Day') : (t('UNIT') || 'Unit')}
                {/* <span style={{ color: '#dc3545' }}>*</span> */}
                <Tooltip content={formData.pricingType === 'Tim' ? (t('HOURS_PER_DAY_TOOLTIP') || 'Enter the number of hours you typically work per day for this project.') : (t('UNIT_TOOLTIP') || 'Enter the number of units for this line item.')} position="top">
                  <span style={{
                    marginLeft: '4px',
                    color: 'var(--Brand-500, #6750B8)',
                    fontFamily: '"Plus Jakarta Sans"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '20px'
                  }}>ⓘ</span>
                </Tooltip>
              </label>
              {formData.pricingType !== 'Tim' ? (() => {
                // Only "Payment per unit" (st) lets the user change the unit; every
                // other non-hourly type is a fixed, disabled "1".
                const unitEditable = formData.pricingType === 'st';
                return (
                <input
                  type="text"
                  placeholder="1"
                  disabled={!unitEditable}
                  style={{
                    height: '44px', padding: '12px 16px', borderRadius: '10px',
                    border: errors.hoursPerDay ? '1px solid #dc3545' : '1px solid #eaebf0',
                    background: unitEditable ? 'white' : '#f8f9fa',
                    fontSize: '15px', fontWeight: 500,
                    color: unitEditable ? '#252525' : '#68727d',
                    fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none', transition: 'all 0.2s ease',
                    cursor: unitEditable ? 'text' : 'not-allowed',
                  }}
                  value={formData.hoursPerDay}
                  onChange={(e) => {
                    if (!unitEditable) return;
                    const value = e.target.value;
                    if (value === '' || /^[1-9]\d*$/.test(value)) {
                      setRawHoursPerDay(undefined);
                      setFormData({ ...formData, hoursPerDay: value });
                      if (errors.hoursPerDay) setErrors({ ...errors, hoursPerDay: '' });
                    }
                  }}
                  onFocus={(e) => { if (!unitEditable) return; e.currentTarget.style.borderColor = errors.hoursPerDay ? '#dc3545' : '#6750b8'; e.currentTarget.style.boxShadow = `0 0 0 3px ${errors.hoursPerDay ? 'rgba(220,53,69,0.1)' : 'rgba(103,80,184,0.1)'}`; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = errors.hoursPerDay ? '#dc3545' : '#eaebf0'; e.currentTarget.style.boxShadow = 'none'; }}
                />
                );
              })() : (() => {
                const hpdDisabled = formData.pricingType !== 'Tim';
                const hpdParts = (formData.hoursPerDay || '').split(':');
                const hpdHours = hpdParts[0] || '';
                const rawMin = hpdParts[1] || '00';
                const hpdMin = ['00', '15', '30', '45'].includes(rawMin) ? rawMin : '00';
                const hpdField: React.CSSProperties = {
                  height: '44px', padding: '12px 14px', borderRadius: '10px',
                  border: errors.hoursPerDay ? '1px solid #dc3545' : '1px solid #eaebf0',
                  background: hpdDisabled ? '#f8f9fa' : 'white', fontSize: '15px', fontWeight: 500,
                  color: hpdDisabled ? '#68727d' : '#252525', fontFamily: "'Plus Jakarta Sans', sans-serif",
                  outline: 'none', transition: 'all 0.2s ease', boxSizing: 'border-box',
                };
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <input
                        type="text" inputMode="numeric" placeholder="08" disabled={hpdDisabled}
                        value={hpdHours}
                        onChange={(e) => {
                          if (hpdDisabled) return;
                          const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                          if (v === '' || parseInt(v) <= 23) {
                            setRawHoursPerDay(undefined);
                            setFormData({ ...formData, hoursPerDay: `${v}:${hpdMin}` });
                            if (errors.hoursPerDay) setErrors({ ...errors, hoursPerDay: '' });
                          }
                        }}
                        onBlur={(e) => {
                          if (hpdDisabled) return;
                          const padded = (e.target.value || '0').padStart(2, '0');
                          const curMin = ((formData.hoursPerDay || '').split(':')[1]) || '00';
                          setFormData({ ...formData, hoursPerDay: `${padded}:${curMin}` });
                        }}
                        style={{ ...hpdField, width: '100%', paddingRight: '46px', cursor: hpdDisabled ? 'not-allowed' : 'text' }}
                      />
                      <span style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#9a94b8', fontSize: '12px', fontWeight: 600, pointerEvents: 'none' }}>hrs</span>
                    </div>
                    <span style={{ color: '#252525', fontWeight: 700, fontSize: '16px' }}>:</span>
                    <div className="custom-dropdown" style={{ position: 'relative', flex: 1 }}>
                      <div
                        ref={minuteTriggerRef}
                        role="button"
                        tabIndex={hpdDisabled ? -1 : 0}
                        aria-disabled={hpdDisabled}
                        aria-expanded={isMinuteDropdownOpen}
                        onClick={() => { if (hpdDisabled) return; if (isMinuteDropdownOpen) setIsMinuteDropdownOpen(false); else openMinuteMenu(); }}
                        onKeyDown={(e) => { if (!hpdDisabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); if (isMinuteDropdownOpen) setIsMinuteDropdownOpen(false); else openMinuteMenu(); } }}
                        onMouseEnter={(e) => { if (!hpdDisabled && !isMinuteDropdownOpen) e.currentTarget.style.borderColor = '#b8b0d4'; }}
                        onMouseLeave={(e) => { if (!isMinuteDropdownOpen) e.currentTarget.style.borderColor = errors.hoursPerDay ? '#dc3545' : '#eaebf0'; }}
                        style={{
                          ...hpdField, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                          paddingRight: '14px', cursor: hpdDisabled ? 'not-allowed' : 'pointer',
                          borderColor: isMinuteDropdownOpen ? '#6750b8' : (errors.hoursPerDay ? '#dc3545' : '#eaebf0'),
                          boxShadow: isMinuteDropdownOpen ? '0 0 0 3px rgba(103,80,184,0.1)' : 'none',
                        }}
                      >
                        <span>{hpdMin} min</span>
                        <svg
                          style={{ transform: isMinuteDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', flexShrink: 0 }}
                          width="20" height="20" viewBox="0 0 20 20" fill="none"
                        >
                          <path d="M5 7.5L10 12.5L15 7.5" stroke="#7c3aed" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      {isMinuteDropdownOpen && !hpdDisabled && minMenuPos && createPortal(
                        <div
                          ref={minuteMenuRef}
                          style={{
                            position: 'fixed', top: minMenuPos.top, left: minMenuPos.left, width: minMenuPos.width,
                            background: 'white', borderRadius: '12px', border: '1px solid #eee7fb',
                            boxShadow: '0 12px 32px rgba(124, 58, 237, 0.20)', zIndex: 100001,
                            padding: '6px', overflow: 'hidden',
                          }}
                        >
                          {['00', '15', '30', '45'].map((m) => {
                            const selected = hpdMin === m;
                            return (
                              <div
                                key={m}
                                onClick={() => {
                                  const curHours = ((formData.hoursPerDay || '').split(':')[0] || '00').padStart(2, '0');
                                  setRawHoursPerDay(undefined);
                                  setFormData({ ...formData, hoursPerDay: `${curHours}:${m}` });
                                  if (errors.hoursPerDay) setErrors({ ...errors, hoursPerDay: '' });
                                  setIsMinuteDropdownOpen(false);
                                }}
                                onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = '#f6f3fd'; }}
                                onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                  padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                                  fontSize: '15px', fontWeight: selected ? 600 : 500,
                                  color: selected ? '#6750b8' : '#252525',
                                  background: selected ? 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(168,85,247,0.10))' : 'transparent',
                                  fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'background 0.15s ease',
                                }}
                              >
                                <span>{m} min</span>
                                {selected && (
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 6L9 17l-5-5" />
                                  </svg>
                                )}
                              </div>
                            );
                          })}
                        </div>,
                        document.body
                      )}
                    </div>
                  </div>
                );
              })()}
              {errors.hoursPerDay && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.hoursPerDay}
                </div>
              )}
            </div>

            {/* Hourly Price */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                color: 'var(--text-icons-light-base-main, #252525)',
                fontFamily: '"Plus Jakarta Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '20px',
                display: 'flex',
                alignItems: 'center',
                // gap: '6px'
              }}>
                {t('PRICE') || 'Hourly Price'}
                {/* <span style={{ color: '#dc3545' }}>*</span> */}
                <Tooltip content={t('HOURLY_PRICE_TOOLTIP') || 'Set the amount you charge per hour of work.'} position="top">
                  <span style={{
                    marginLeft: '4px',
                    color: 'var(--Brand-500, #6750B8)',
                    fontFamily: '"Plus Jakarta Sans"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '20px'
                  }}>ⓘ</span>
                </Tooltip>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder={t('ENTER_AMOUNT') || 'Enter amount'}
                  style={{
                    width: '100%',
                    height: '44px',
                    padding: '12px 50px 12px 16px',
                    borderRadius: '10px',
                    border: errors.hourlyPrice ? '1px solid #dc3545' : '1px solid #eaebf0',
                    background: 'white',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#252525',
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    outline: 'none',
                    transition: 'all 0.2s ease'
                  }}
                  value={formData.hourlyPrice}
                  inputMode="decimal"
                  onKeyDown={(e) => {
                    if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                      e.preventDefault();
                    }
                  }}
                  onChange={(e) => {
                    const sanitized = sanitizePriceInput(e.target.value);
                    if (sanitized !== null) {
                      setFormData({ ...formData, hourlyPrice: sanitized });
                      if (errors.hourlyPrice) setErrors({ ...errors, hourlyPrice: '' });
                    }
                  }}
                  onFocus={(e) => {
                    if (!errors.hourlyPrice) {
                      e.currentTarget.style.borderColor = '#6750b8';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(103, 80, 184, 0.1)';
                    } else {
                      e.currentTarget.style.borderColor = '#dc3545';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.1)';
                    }
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = errors.hourlyPrice ? '#dc3545' : '#eaebf0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                  onMouseEnter={(e) => {
                    if (document.activeElement !== e.currentTarget && !errors.hourlyPrice) {
                      e.currentTarget.style.borderColor = '#b8b0d4';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (document.activeElement !== e.currentTarget) {
                      e.currentTarget.style.borderColor = errors.hourlyPrice ? '#dc3545' : '#eaebf0';
                    }
                  }}
                />
                <span style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#68727d',
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}>
                  {currency}
                </span>
              </div>
              {errors.hourlyPrice && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.hourlyPrice}
                </div>
              )}
            </div>
          </div>

         
 {/* Pricing Type - Full Width */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <label style={{
              color: 'var(--text-icons-light-base-main, #252525)',
              fontFamily: '"Plus Jakarta Sans"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 600,
              lineHeight: '20px',
              display: 'flex',
              alignItems: 'center',
              // gap: '6px'
            }}>
              {t('PRICING_TYPE') || 'Price Type'}
              <Tooltip content={t('PRICING_TYPE_TOOLTIP') || 'Choose how your pricing is calculated'} position="top">
                <span style={{ 
                  marginLeft: '4px', 
                  color: 'var(--Brand-500, #6750B8)', 
                  fontFamily: '"Plus Jakarta Sans"', 
                  fontSize: '14px', 
                  fontStyle: 'normal',
                  fontWeight: 700, 
                  lineHeight: '20px'
                }}>ⓘ</span>
              </Tooltip>
            </label>
            <div className="custom-dropdown" ref={pricingTypeDropdownRef} style={{ position: 'relative' }}>
              <div
                style={{
                  height: '44px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: '1px solid #eaebf0',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#252525',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  transition: 'all 0.2s ease'
                }}
                onClick={() => setIsPricingTypeDropdownOpen(!isPricingTypeDropdownOpen)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#b8b0d4';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#eaebf0';
                }}
              >
                <span>
                  {t(PRICING_TYPE_OPTIONS.find(opt => opt.value === formData.pricingType)?.articleName || 'HOURLY_WAGES')}
                </span>
                <svg
                  style={{
                    transform: isPricingTypeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s'
                  }}
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#68727d"
                    strokeWidth="1.67"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {isPricingTypeDropdownOpen && (
                <div style={{
                  position: 'absolute',
                  bottom: 'calc(100% + 4px)', // open upward so it isn't hidden below the modal
                  left: 0,
                  right: 0,
                  background: 'white',
                  borderRadius: '10px',
                  border: '1px solid #eaebf0',
                  boxShadow: '0px -6px 20px rgba(124, 58, 237, 0.16)',
                  zIndex: 1000,
                  maxHeight: '200px',
                  overflowY: 'auto'
                }}>
                  {PRICING_TYPE_OPTIONS.map((option) => (
                    <div
                      key={option.id}
                      style={{
                        padding: '12px 16px',
                        cursor: 'pointer',
                        fontSize: '15px',
                        fontWeight: 500,
                        color: formData.pricingType === option.value ? '#6750b8' : '#252525',
                        background: formData.pricingType === option.value ? '#f4f3f9' : 'white',
                        fontFamily: "'Plus Jakarta Sans', sans-serif"
                      }}
                      onMouseEnter={(e) => {
                        if (formData.pricingType !== option.value) {
                          e.currentTarget.style.background = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (formData.pricingType !== option.value) {
                          e.currentTarget.style.background = 'white';
                        }
                      }}
                      onClick={() => {
                        setFormData({ ...formData, pricingType: option.value });
                        setIsPricingTypeDropdownOpen(false);
                      }}
                    >
                      {t(option.articleName)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          </div>{/* END STEP: work */}

          {/* STEP: Pension & Holiday */}
          <div style={paneStyle('pension')}>

          {/* Pension & Holiday — only in the global modal, for a specific freelancer (never "All") */}
          {showFreelancerSelector && selectedFreelancerTarget !== 'all' && onPensionChange && onHolidayChange && (
            <ModalPensionHolidaySection
              freelancerId={selectedFreelancerTarget}
              freelancerName={freelancerOptions.find((o) => o.value === selectedFreelancerTarget)?.label}
              isRegularEngagement={isRegularEngagement}
              globalData={globalData}
              calculatedAmount={
                calculatedSubAmount?.response?.freelancersData?.find(
                  (fd: any) => fd.createdBy?.toString() === selectedFreelancerTarget
                )?.grossInSek || 0
              }
              t={t}
              pensionData={draftPensionData}
              holidayData={draftHolidayData}
              onPensionChange={setDraftPensionData}
              onHolidayChange={setDraftHolidayData}
            />
          )}

          </div>{/* END STEP: pension */}

          {/* STEP: Service description */}
          <div style={paneStyle('service')}>

          {/* Service Name - Full Width */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <label style={{
                color: 'var(--text-icons-light-base-main, #252525)',
                fontFamily: '"Plus Jakarta Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '20px',
                display: 'flex',
                alignItems: 'center',
                // gap: '6px'
              }}>
                {t('SERVICE_DESCRIPTION') || 'Service Name'}
                {/* <span style={{ color: '#dc3545' }}>*</span> */}
                <Tooltip content={t('SERVICE_DESCRIPTION_TOOLTIP') || 'Provide a brief description of the work or service performed.'} position="top">
                  <span style={{
                    marginLeft: '4px',
                    color: 'var(--Brand-500, #6750B8)',
                    fontFamily: '"Plus Jakarta Sans"',
                    fontSize: '14px',
                    fontStyle: 'normal',
                    fontWeight: 700,
                    lineHeight: '20px'
                  }}>ⓘ</span>
                </Tooltip>
              </label>
              <input
                type="text"
                placeholder={t('ENTER_SERVICE_DESCRIPTION') || 'Enter service name'}
                style={{
                  height: '44px',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  border: errors.serviceDescription ? '1px solid #dc3545' : '1px solid #eaebf0',
                  background: 'white',
                  fontSize: '15px',
                  fontWeight: 500,
                  color: '#252525',
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  outline: 'none',
                  transition: 'all 0.2s ease'
                }}
                value={formData.serviceDescription}
                maxLength={42}
                onChange={(e) => {
                  const value = e.target.value;
                  const latinPattern = /^[A-Za-zÅÄÖåäö0-9\s\.,!?'"\-()&]*$/;

                  if (value.length <= 42 && (value === '' || latinPattern.test(value))) {
                    setFormData({ ...formData, serviceDescription: value });
                    if (errors.serviceDescription) setErrors({ ...errors, serviceDescription: '' });
                  }
                }}
                onFocus={(e) => {
                  if (!errors.serviceDescription) {
                    e.currentTarget.style.borderColor = '#6750b8';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(103, 80, 184, 0.1)';
                  } else {
                    e.currentTarget.style.borderColor = '#dc3545';
                    e.currentTarget.style.boxShadow = '0 0 0 3px rgba(220, 53, 69, 0.1)';
                  }
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = errors.serviceDescription ? '#dc3545' : '#eaebf0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (document.activeElement !== e.currentTarget && !errors.serviceDescription) {
                    e.currentTarget.style.borderColor = '#b8b0d4';
                  }
                }}
                onMouseLeave={(e) => {
                  if (document.activeElement !== e.currentTarget) {
                    e.currentTarget.style.borderColor = errors.serviceDescription ? '#dc3545' : '#eaebf0';
                  }
                }}
              />
              {errors.serviceDescription && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                  {errors.serviceDescription}
                </div>
              )}
              <small style={{ fontSize: '12px', color: '#68727d', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                {formData.serviceDescription.length}/42 {t('CHARACTERS') || 'characters'}
              </small>
            </div>

          {/* Summary Box */}
          {selectedDates.length > 0 && formData.hoursPerDay && formData.hourlyPrice && (
            <div className="ag-summary" style={{
              padding: '16px',
              borderRadius: '10px',
              background: '#f4f3f9',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px'
            }}>
              <h4 style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '20px',
                color: '#252525',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('AUTO_GENERATE_ITEMS') || 'Auto Generate Line Items'}
              </h4>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 300,
                lineHeight: '20px',
                color: '#68727d',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('SUMMARY_GENERATE_TEXT')} {selectedDates.length} {selectedDates.length !== 1 ? t('SUMMARY_LINE_ITEMS') : t('SUMMARY_LINE_ITEM')}, {t('SUMMARY_EACH_WITH')} {formData.hoursPerDay} {t('SUMMARY_HOURS_AT')} {formData.hourlyPrice} {currency}/{formData.pricingType || 'Tim'}
              </p>
              <p style={{
                margin: 0,
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '20px',
                color: '#6750b8',
                fontFamily: "'Plus Jakarta Sans', sans-serif"
              }}>
                {t('ESTIMATED_TOTAL')}: {(() => {
                  const [hours, minutes] = formData.hoursPerDay.split(':').map(Number);
                  const totalHours = hours + (minutes / 60);
                  const pricePerItem = totalHours * parseFloat(formData.hourlyPrice || '0');
                  const total = pricePerItem * selectedDates.length;
                  return total.toFixed(2);
                })()} {currency}
              </p>
            </div>
          )}

          </div>{/* END STEP: service */}

          {/* STEP: Review */}
          {showFreelancerSelector && (
            <div style={paneStyle('review')}>
              <ModalGenerateReview
                freelancerOptions={freelancerOptions}
                target={selectedFreelancerTarget}
                existingRowsByFreelancer={existingRowsByFreelancer}
                pensionData={draftPensionData}
                holidayData={draftHolidayData}
                selectedDates={selectedDates}
                formData={formData}
                currency={currency}
                t={t}
              />
            </div>
          )}
        </div>

        {/* Footer - Fixed */}
        <div className="auto-generate-footer" style={{ 
          padding: '16px 24px',
          borderTop: '1px solid #eaebf0',
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '12px',
          flexShrink: 0
        }}>
          {showFreelancerSelector && activeStep > 0 && (
            <button
              onClick={handleBack}
              style={{
                height: '40px',
                padding: '10px 24px',
                borderRadius: '25px',
                border: '1px solid #6750b8',
                background: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                color: '#6750b8',
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                marginRight: 'auto'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f4f3f9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              {t('BACK') || 'Back'}
            </button>
          )}
          <button
            onClick={handleCancel}
            style={{
              height: '40px',
              padding: '10px 24px',
              borderRadius: '25px',
              border: '1px solid #6750b8',
              background: 'white',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              color: '#6750b8',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f4f3f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'white';
            }}
          >
            {t('CANCEL') || 'Cancel'}
          </button>
          <button
            onClick={showFreelancerSelector ? handleNext : handleGenerate}
            style={{
              height: '40px',
              padding: '10px 24px',
              borderRadius: '25px',
              border: 'none',
              background: '#6750b8',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 600,
              color: 'white',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#544196';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#6750b8';
            }}
          >
            {showFreelancerSelector && activeKey !== 'review' ? (
              <>
                {t('NEXT') || 'Next'}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M7.5 5L12.5 10L7.5 15" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M8.65185 1.56501C8.78087 1.25958 9.2137 1.25958 9.34273 1.56501L11.3882 6.4069C11.4262 6.49684 11.4977 6.56842 11.5877 6.60642L16.4296 8.65185C16.735 8.78087 16.735 9.2137 16.4296 9.34273L11.5877 11.3882C11.4977 11.4262 11.4262 11.4977 11.3882 11.5877L9.34273 16.4296C9.2137 16.735 8.78087 16.735 8.65185 16.4296L6.60642 11.5877C6.56842 11.4977 6.49684 11.4262 6.4069 11.3882L1.56501 9.34273C1.25958 9.2137 1.25958 8.78087 1.56501 8.65185L6.4069 6.60642C6.49684 6.56842 6.56842 6.49684 6.60642 6.4069L8.65185 1.56501Z" stroke="white" strokeWidth="2"/>
                </svg>
                {t('GENERATES') || 'Generate Items'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
    </>,
    document.body
  );
};

export default AutoGenerateItemsModal;
